import {Injectable, OnDestroy} from '@angular/core';
import {Params, Router} from '@angular/router';
import {
  AppContainerOptions,
  ContentSideNavButton,
  LeftPaneContent,
  LinkSideNavButton,
  SideNavBarService as ApSideNavBarService,
  SideNavButton
} from '@mydocma/app-container';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';
import {BpRoutingService} from '../bp-routing/bp-routing.service';
import {StructureComponent, BpViewService} from '@mydocma/co/src/lib/common';
import {SideNavBarButtonsEnum} from './sidenavbar-buttons.enum';
import {BpProjectPermissions} from '../bp-project/bp-project-permissions';
import { CoPermissionArea, CoPermissionsService, CoPermissionType } from '@mydocma/co';

@UntilDestroy()
@Injectable()
export class SideNavBarService implements OnDestroy {

  private readonly idToButtonMap = new Map<string, SideNavButton>([
    [SideNavBarButtonsEnum.ViewGroup, new ContentSideNavButton({
      id: SideNavBarButtonsEnum.ViewGroup,
      icon: 'visibility',
      expanded: true
    })],
    [SideNavBarButtonsEnum.ViewStructure, new ContentSideNavButton({
      id: SideNavBarButtonsEnum.ViewStructure,
      parentId: SideNavBarButtonsEnum.ViewGroup,
      icon: 'account_tree',
      active: true,
      content: new LeftPaneContent({
        id: 'structure',
        componetType: StructureComponent,
      })
    })],
    [SideNavBarButtonsEnum.BimModels, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.BimModels,
      icon: '_bim-model',
    })],
    [SideNavBarButtonsEnum.Sections, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Sections,
      icon: '_bim-section',
    })],
    [SideNavBarButtonsEnum.Plans, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Plans,
      icon: '_plan_without_pin',
    })],
    [SideNavBarButtonsEnum.Missions, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Missions,
      icon: '_journey',
    })]
  ]);

  private idToUrlMap = new Map<string, string>([
    [SideNavBarButtonsEnum.BimModels, '/projects/:projectId/bim-models'],
    [SideNavBarButtonsEnum.Sections, '/projects/:projectId/sections'],
    [SideNavBarButtonsEnum.Plans, '/projects/:projectId/plans'],
    [SideNavBarButtonsEnum.Missions, '/missions']
  ]);

  private readonly idToTranslationKey = new Map<string, string>([
    [SideNavBarButtonsEnum.ViewGroup, 'CO.sidebar.view'],
    [SideNavBarButtonsEnum.ViewStructure, 'CO.sidebar.structure'],
    [SideNavBarButtonsEnum.BimModels, 'CO.sidebar.bimModels'],
    [SideNavBarButtonsEnum.Sections, 'CO.sidebar.sections'],
    [SideNavBarButtonsEnum.Plans, 'CO.sidebar.plans'],
    [SideNavBarButtonsEnum.Missions, 'CO.sidebar.routes'],
  ]);
  private buttons: SideNavButton[] = [];
  private appContainerOptions: AppContainerOptions;

  private lastRouteParams: Params;
  private lastRoute: string;


  constructor(
    private readonly translateService: TranslateService,
    private readonly routingService: BpRoutingService,
    private readonly sidNavBarService: ApSideNavBarService,
    private readonly bpViewService: BpViewService,
    private readonly router: Router,
    private readonly bpPermissionService: CoPermissionsService
  ) {

    this.bpViewService.currentProject$
      .pipe(untilDestroyed(this))
      .subscribe(project => {
        if (project === null || project === undefined) {
          this.router.navigate(['projects']);
        } else {
          this.initButtons();
        }
      });

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(l => this.transalteButtons());


    this.routingService.moduleActivatedRoute$
      .pipe(
        untilDestroyed(this)
      ).subscribe(async (mr) => {
      if (mr) {
        const p = await this.routingService.getRoutePath(mr.route.snapshot);
        this.lastRouteParams = mr.params;
        this.lastRoute = p;
        this.onRouteChanged(p);
      }
    });

  }

  ngOnDestroy(): void {
  }

  register(appContainer: AppContainerOptions) {
    this.appContainerOptions = appContainer;
    appContainer.sideNavBar.buttons = this.buttons;
  }

  unregister(appContainer: AppContainerOptions) {
    appContainer.sideNavBar.buttons = [];
    this.appContainerOptions = null;
  }

  private async onRouteChanged(route: string) {
    for (let b of this.buttons) {
      if (b instanceof LinkSideNavButton) {
        const buttonPath = this.idToUrlMap.get(b.id);
        b.active = buttonPath === route;
      }
    }
  }

  private async initButtons() {
    this.transalteButtons();
    if (this.buttons.length === 0) {
      for (const btn of this.idToButtonMap.values()) {
        if (BpProjectPermissions.includes(btn.id)) {
          btn.hidden = !await this.checkButtonVisibility(btn);
          const buttonPath = this.idToUrlMap.get(btn.id);
          btn.active = buttonPath === this.lastRoute;
        }
        this.buttons.push(btn);
        if (btn instanceof LinkSideNavButton) {
          btn.click.subscribe(b => this.onLinkButtonClick(btn.id));
        }
      }
      ;
    } else {
      for (const btn of this.buttons) {
        if (BpProjectPermissions.includes(btn.id)) {
          btn.hidden = !await this.checkButtonVisibility(btn);
        }
      }
      ;
    }
  }

  private transalteButtons() {
    this.idToTranslationKey.forEach((key, id) => {
      const btn = this.idToButtonMap.get(id);
      if (btn && key) {
        btn.text = this.translateService.instant(key);
        btn.tooltip = this.translateService.instant(key);
      }
    });
  }

  private async onLinkButtonClick(btn) {
    if (btn) {
      let btnRoute = this.idToUrlMap.get(btn);
      if (btnRoute) {
        if (this.lastRouteParams?.projectId) {
          btnRoute = btnRoute.replace(':projectId', this.lastRouteParams.projectId);
        }
        this.routingService.navigate([btnRoute]);
      }
      //    this.sidNavBarService.collapseBar();
    }

  }

  private async checkButtonVisibility(btn: SideNavButton): Promise<boolean> {
    if(this.bpPermissionService.hasProjectPermission(btn.id as CoPermissionArea, CoPermissionType.Visible)) return true;
    return false;
  }

}
